import { useMemo } from "react";
import { Button, Input, Form, ModalFooter, errorToast } from "@telluscompany/design-system";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocalization } from "@i18n";
import { editStructureSchema, type EditStructureFormData } from "../../schemas";
import { useGetStructureByIdQuery } from "../../api/structures/structures.queries";
import { LoadingBlurDiv } from "@shared/components";
import { AxiosError } from "axios";
import { ReadOnlyField } from "@shared/components";

interface EditStructureFormProps {
  tenantId: string;
  structureId: string;
  onSubmit: (data: EditStructureFormData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export const EditStructureForm = ({
  tenantId,
  structureId,
  onSubmit,
  onCancel,
  isSubmitting,
}: EditStructureFormProps) => {
  const { translate } = useLocalization();

  const { data: structureData, isLoading, error, isError } = useGetStructureByIdQuery(
    tenantId,
    structureId,
  );

  if (isError) {
    const axiosError = error as AxiosError;
    errorToast({
      description: axiosError.parsedMessage ?? translate("common.errorEditing"),
    });
    onCancel();
  }

  const formValues = useMemo(
    () => (structureData ? { name: structureData.name } : undefined),
    [structureData],
  );

  const form = useForm<EditStructureFormData>({
    resolver: zodResolver(editStructureSchema(translate)),
    defaultValues: {
      name: "",
    },
    values: formValues,
  });

  return (
    <LoadingBlurDiv isLoading={isLoading}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="px-[32px] py-[16px] max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-1 gap-4">
              <div>
              <ReadOnlyField
                label={translate("management.structures.form.label.complexName")}
                value={structureData?.complexName}
              />
              </div>
              <div>
                <Input
                  control={form.control}
                  label={translate("management.structures.form.label.name")}
                  name="name"
                  type="text"
                  placeholder={translate(
                    "management.structures.form.placeholder.name",
                  )}
                />
              </div>
            </div>
          </div>
          <ModalFooter
            footer={
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {translate("management.structures.button.save")}
              </Button>
            }
            closeModal={
              <Button type="button" variant="secondary" onClick={onCancel}>
                {translate("management.structures.button.cancel")}
              </Button>
            }
          />
        </form>
      </Form>
    </LoadingBlurDiv>
  );
};
